import React from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { BrandLogo } from './BrandLogo';
import { fpColors } from '../styles/theme';

/**
 * Shared layout for Fidelity Purple auth screens
 * (Login, Register, ForgotPassword, EmailVerification, ResetPassword).
 *
 * Renders the purple-tinted page background, two decorative blurred circles,
 * and a centered white card. On mobile the card stacks a brand header + form
 * column. On desktop (>=1024px) an optional `heroSlot` renders as a left
 * column with primary-container background.
 */

const DESKTOP_BREAKPOINT = 1024;

interface AuthShellProps {
  children: React.ReactNode;
  /**
   * Desktop-only hero (left column). If omitted, the shell renders as a
   * single centered card.
   */
  heroSlot?: React.ReactNode;
  /** Show a back button in the top-left of the card. */
  showBack?: boolean;
  onBack?: () => void;
  /** Show the mobile brand header above the form. Default true. */
  showMobileBrand?: boolean;
}

export const AuthShell: React.FC<AuthShellProps> = ({
  children,
  heroSlot,
  showBack = false,
  onBack,
  showMobileBrand = true,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT && !!heroSlot;

  return (
    <View style={styles.page}>
      <View pointerEvents="none" style={[styles.blur, styles.blurTopLeft]} />
      <View pointerEvents="none" style={[styles.blur, styles.blurBottomRight]} />

      <KeyboardAvoidingView
        style={styles.kav}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scroll,
            isDesktop && styles.scrollDesktop,
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View
            style={[
              styles.shell,
              isDesktop ? styles.shellDesktop : styles.shellMobile,
            ]}
          >
            {isDesktop && <View style={styles.heroColumn}>{heroSlot}</View>}

            <View style={[styles.formCol, isDesktop && styles.formColDesktop]}>
              {(showBack || (!isDesktop && showMobileBrand)) && (
                <View style={styles.topRow}>
                  {showBack ? (
                    <Pressable
                      onPress={onBack}
                      style={styles.backButton}
                      hitSlop={8}
                      accessibilityRole="button"
                      accessibilityLabel="Back"
                    >
                      <MaterialIcons
                        name="arrow-back"
                        size={22}
                        color={fpColors.onSurfaceVariant}
                      />
                    </Pressable>
                  ) : (
                    <View />
                  )}
                </View>
              )}

              {!isDesktop && showMobileBrand && <MobileBrand />}

              <View style={styles.formInner}>{children}</View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

/** Mobile-only brand header: mark on primary-container tile + wordmark */
const MobileBrand: React.FC = () => (
  <View style={styles.mobileBrand}>
    <View style={styles.mobileBrandMark}>
      <BrandLogo
        variant="mark"
        tone="mono"
        tint={fpColors.onPrimaryContainer}
        height={28}
      />
    </View>
    <BrandLogo
      variant="horizontal"
      height={22}
      tint={fpColors.primary}
      tone="mono"
    />
  </View>
);

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  kav: { flex: 1 },
  scroll: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  scrollDesktop: {
    padding: 32,
  },
  shell: {
    width: '100%',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.08,
    shadowRadius: 30,
    elevation: 6,
  },
  shellMobile: {
    maxWidth: 440,
  },
  shellDesktop: {
    maxWidth: 1120,
    minHeight: 700,
    flexDirection: 'row',
  },
  heroColumn: {
    flex: 1,
    backgroundColor: fpColors.primaryContainer,
  },
  formCol: {
    padding: 20,
    justifyContent: 'center',
  },
  formColDesktop: {
    flex: 1,
    padding: 48,
  },
  formInner: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileBrand: {
    alignItems: 'center',
    marginBottom: 12,
    gap: 6,
  },
  mobileBrandMark: {
    width: 52,
    height: 52,
    borderRadius: 12,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 4,
  },
  blur: {
    position: 'absolute',
    width: 256,
    height: 256,
    borderRadius: 128,
    opacity: 0.3,
  },
  blurTopLeft: {
    top: 80,
    left: -80,
    backgroundColor: fpColors.primary,
  },
  blurBottomRight: {
    bottom: 80,
    right: -80,
    backgroundColor: fpColors.tertiaryContainer,
  },
});
